/**
 * Sound. Kenney's CC0 clips, one AudioContext, no library.
 *
 * What sound is *for* here: the pitch's whole argument is that a stranger's reaction is the content,
 * and a reaction you can only see is half a reaction. The spring, the marbles, the moment a plate
 * turns out to be paint — those are the beats a clip needs on its audio track to read on mute-off
 * autoplay feeds. So the sound set maps one-to-one onto `RaidEvent`, the same list the captions and
 * the replay timeline are built from. Adding a trap means adding a line to `EVENT_SFX`, not
 * inventing an audio design.
 *
 * Three constraints shaped the implementation:
 *
 *  1. **Never block, never break.** Every clip is fetched and decoded lazily, failures are swallowed
 *     to a no-op, and a browser that refuses to give us an AudioContext just gets a silent game.
 *     Audio is not allowed to be the reason a raid does not start.
 *  2. **No autoplay violations.** Browsers only allow an AudioContext to run after a real gesture,
 *     so we construct it on the first `play()` and `resume()` on the next gesture if it starts
 *     suspended. Entering a raid is always a tap, so in practice the first trap already has sound.
 *  3. **No `Math.random`.** Variation (footstep choice, pitch jitter) comes from a counter through
 *     the project's hash, per the studio rule that nothing procedural may be non-deterministic.
 *
 * Assets are built by tools/build-audio.sh. See THIRD_PARTY.md.
 */

import { hashFloat } from '../core/rng'
import type { RaidEventType } from '../sim/types'

/** Sound files in public/sfx. Names are the asset filenames minus `.m4a`. */
export type SfxName =
  | 'launch' | 'swat' | 'push' | 'slip' | 'glue' | 'pit'
  | 'decoy' | 'bluff' | 'pickup' | 'escape' | 'fail'
  | 'land' | 'click'
  | 'step0' | 'step1' | 'step2' | 'step3'

/**
 * Which trap makes which noise. Deliberately the same shape as `CAPTIONS` in raidMode: an event
 * either has a voice in the game's language or it doesn't.
 *
 * `hesitate` is intentionally silent. It is the owner's most valuable datum, but beeping at a player
 * for standing still would punish exactly the careful play the room is trying to provoke.
 */
export const EVENT_SFX: Partial<Record<RaidEventType, SfxName>> = {
  launched: 'launch',
  swatted: 'swat',
  pushed: 'push',
  slipped: 'slip',
  glued: 'glue',
  pitfall: 'pit',
  decoy: 'decoy',
  bluffed: 'bluff',
  pickup: 'pickup',
  escaped: 'escape',
  failed: 'fail',
}

/** Per-clip gain. Traps should land; footsteps should sit under everything. */
const GAIN: Partial<Record<SfxName, number>> = {
  launch: 1,
  pit: 0.9,
  swat: 0.85,
  glue: 0.7,
  slip: 0.7,
  push: 0.55,
  land: 0.4,
  click: 0.35,
  step0: 0.22,
  step1: 0.22,
  step2: 0.22,
  step3: 0.22,
}

const MUTE_KEY = 'tr.muted'
const STEPS: SfxName[] = ['step0', 'step1', 'step2', 'step3']

class Sfx {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private buffers = new Map<SfxName, AudioBuffer | null>()
  private pending = new Map<SfxName, Promise<AudioBuffer | null>>()
  private muted = readMuted()
  /** Drives deterministic variation. Not a random seed — just a monotonic tick. */
  private tick = 0
  private unsupported = false

  get isMuted(): boolean {
    return this.muted
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    if (this.master) this.master.gain.value = muted ? 0 : 1
    try {
      localStorage.setItem(MUTE_KEY, muted ? '1' : '0')
    } catch {
      // Private mode. The setting just won't survive a reload.
    }
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted)
    return this.muted
  }

  /** Fetch and decode ahead of time. Call when entering a raid; never await it. */
  warm(names: readonly SfxName[]): void {
    for (const n of names) void this.buffer(n)
  }

  /** Warm everything a raid can possibly need, including footsteps. */
  warmRaid(): void {
    this.warm([...Object.values(EVENT_SFX), ...STEPS, 'land'] as SfxName[])
  }

  play(name: SfxName, opts: { gain?: number; rate?: number } = {}): void {
    if (this.muted || this.unsupported) return
    const ctx = this.context()
    if (!ctx) return
    const buf = this.buffers.get(name)
    if (buf === undefined) {
      // Not decoded yet. Start it, but do not play late — a trap sound arriving 400 ms after the
      // trap is worse than no sound.
      void this.buffer(name)
      return
    }
    if (buf === null) return
    try {
      const src = ctx.createBufferSource()
      src.buffer = buf
      src.playbackRate.value = opts.rate ?? 1
      const g = ctx.createGain()
      g.gain.value = (opts.gain ?? 1) * (GAIN[name] ?? 0.8)
      src.connect(g).connect(this.master!)
      src.start()
    } catch {
      // A context that died under us (tab suspended, device changed). Silence, not a crash.
    }
  }

  /**
   * A footstep, chosen and pitched deterministically. Called by the raid mode once per stride
   * rather than per frame — see `RaidMode.footsteps`.
   */
  step(): void {
    const i = this.tick++
    const name = STEPS[i % STEPS.length] as SfxName
    // ±6% pitch so a run does not sound like a metronome.
    this.play(name, { rate: 0.94 + hashFloat(i, 7) * 0.12 })
  }

  /** Resume a context that the browser started suspended. Safe to call on every gesture. */
  resume(): void {
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume().catch(() => {})
  }

  private context(): AudioContext | null {
    if (this.ctx) return this.ctx
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (!Ctor) {
        this.unsupported = true
        return null
      }
      this.ctx = new Ctor()
      this.master = this.ctx.createGain()
      this.master.gain.value = this.muted ? 0 : 1
      this.master.connect(this.ctx.destination)
      this.resume()
      return this.ctx
    } catch {
      this.unsupported = true
      return null
    }
  }

  private buffer(name: SfxName): Promise<AudioBuffer | null> {
    const done = this.pending.get(name)
    if (done) return done
    const job = (async () => {
      const ctx = this.context()
      if (!ctx) return null
      try {
        const res = await fetch(`sfx/${name}.m4a`)
        if (!res.ok) throw new Error(`sfx/${name}.m4a → ${res.status}`)
        const bytes = await res.arrayBuffer()
        const buf = await ctx.decodeAudioData(bytes)
        this.buffers.set(name, buf)
        return buf
      } catch {
        // Cache the failure so we stop retrying every time the trap fires.
        this.buffers.set(name, null)
        return null
      }
    })()
    this.pending.set(name, job)
    return job
  }
}

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1'
  } catch {
    return false
  }
}

export const sfx = new Sfx()
